import { zodResolver } from "@hookform/resolvers/zod";
import { creditProposalProfessionDataSchema } from "@repo/core/helpers/zod.schemas";
import { ROUTES } from "@repo/core/routes";
import { useProposalFlowNavigation } from "../../utils/proposalFlowNavigation";
import { ProfessionDataFormType } from "@repo/core/stores/proposalForm/types";
import { useCreditProposal } from "../hooks/useCreditProposal";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { apiInstance } from "@repo/core/services/instances/api";
import { CommonResource } from "@repo/core/services/resources/common";
import { useMemo } from "react";
import {
  formatDateFromISO,
  resolveAdmissionDateIso,
  resolveIncomeAmount,
  resolveProfessionCode
} from "../hooks/useCreditProposal/mappers";
import { ConsultProposalResponseDTO } from "@repo/core/services/entities/Proposals";
import useToast from "@repo/core/components/core/Toast";

const commonResource = new CommonResource(apiInstance);

export const ProfessionDataFormController = () => {
  const { replace, completeStep, goToPreviousStep } = useProposalFlowNavigation();
  const toast = useToast();
  const {
    updateProposalData,
    proposalData,
    resetCreditProposalStore,
    persistFullProposalSnapshot
  } = useCreditProposal();

  // Get profession data from proposalData
  const professionData = useMemo(() => {
    if (!proposalData) {
      return {
        professionalCategory: "",
        profession: "",
        admissionDate: "",
        income: "0",
        professionalClassDescription: "",
        professionName: ""
      };
    }

    const incomeAmount = resolveIncomeAmount(proposalData);

    return {
      professionalCategory: proposalData.strClasseProfissional || "",
      profession: resolveProfessionCode(proposalData),
      admissionDate:
        formatDateFromISO(resolveAdmissionDateIso(proposalData)) || "",
      income:
        incomeAmount !== undefined && incomeAmount !== null
          ? incomeAmount.toString()
          : "0",
      professionalClassDescription: "",
      professionName: ""
    };
  }, [proposalData]);

  const { data: professionsResponse } = useQuery({
    queryKey: ["professions"],
    queryFn: () => commonResource.fetchProfessions(),
    select: (data) => data.data
  });

  const { data: professionalCategoriesResponse } = useQuery({
    queryKey: ["professionalCategories"],
    queryFn: () => commonResource.fetchProfessionalCategories(),
    select: (data) => data.data
  });

  const { control, handleSubmit } = useForm<ProfessionDataFormType>({
    resolver: zodResolver(creditProposalProfessionDataSchema),
    mode: "onSubmit",
    reValidateMode: "onBlur",
    values: {
      professionalCategory: professionData.professionalCategory,
      profession: professionData.profession,
      admissionDate: professionData.admissionDate,
      income: professionData.income,
      professionalClassDescription: "",
      professionName: ""
    }
  });

  const { professions, professionalCategories } = useMemo(() => {
    const professions = professionsResponse?.map((profession) => ({
      label: profession.strDescricao,
      value: profession.strCodProfissao
    }));

    const professionalCategories = professionalCategoriesResponse?.map(
      (professionalCategory) => ({
        label: professionalCategory.strDescricao,
        value: professionalCategory.strClasse
      })
    );

    return { professions, professionalCategories };
  }, [professionsResponse, professionalCategoriesResponse]);

  const handleOnSubmitForm = handleSubmit(async (formData) => {
    if (proposalData) {
      // Find descriptions for selected codes
      const selectedProfessionalCategory = professionalCategories?.find(
        (cat) => cat.value === formData.professionalCategory
      );
      const selectedProfession = professions?.find(
        (prof) => prof.value === formData.profession
      );

      const professionCode =
        formData.profession ||
        resolveProfessionCode(proposalData) ||
        proposalData.strAtivProfissao ||
        proposalData.strProfissao;
      const admissionIso = formData.admissionDate
        ? new Date(
            formData.admissionDate.split("/").reverse().join("-")
          ).toISOString()
        : resolveAdmissionDateIso(proposalData);
      const incomeValue = formData.income
        ? parseFloat(formData.income)
        : resolveIncomeAmount(proposalData);

      updateProposalData({
        strClasseProfissional:
          formData.professionalCategory || proposalData.strClasseProfissional,
        strProfissao: professionCode,
        strAtivProfissao: professionCode,
        ...(admissionIso
          ? {
              strTrabalhaDesde: admissionIso,
              datAtivDataAdmissao: admissionIso,
              datTrabDesde: admissionIso
            }
          : {}),
        ...(incomeValue !== undefined
          ? { dblComSalario: incomeValue, dblRenda: incomeValue }
          : {}),
        // Store descriptions for display purposes (not sent to backend)
        ...(selectedProfessionalCategory?.label && {
          strClasseProfissionalDescricao: selectedProfessionalCategory.label
        }),
        ...(selectedProfession?.label && {
          strProfissaoDescricao: selectedProfession.label
        })
      } as Partial<ConsultProposalResponseDTO>);
    }

    try {
      await persistFullProposalSnapshot();
    } catch {
      toast.error(
        "Erro ao salvar etapa",
        "Não foi possível salvar os dados desta etapa. Tente novamente."
      );
      return;
    }

    completeStep(ROUTES.creditProposalInvoiceData);
  });

  const handleGoBack = () => {
    goToPreviousStep(ROUTES.creditProposalAddress);
  };

  const handleClose = () => {
    resetCreditProposalStore();
    replace(ROUTES.home);
  };
  return {
    control,
    handleOnSubmitForm,
    handleGoBack,
    handleClose,
    professions,
    professionalCategories
  };
};
